export interface StrokeSectionVideoItem {
  displayOrder?: number;
  sequence?: number;
  title?: string;
  videoUrl?: string;
  text?: string;
  imageUrls?: string[];
  sponsorName?: string;
  sponsorLogoUrl?: string;
}

export interface StrokeVideoSection {
  key: string;
  label: string;
  displayOrder?: number;
  videoUrl?: string;
  text?: string;
  imageUrls?: string[];
  videos?: StrokeSectionVideoItem[];
}

export const STROKE_VIDEO_SECTION_DEFINITIONS = [
  { key: 'stepByStep', label: 'Step by Step', displayOrder: 1 },
  { key: 'beginnerMistakes', label: 'Beginner Mistakes', displayOrder: 2 },
  { key: 'intermediateMistakes', label: 'Intermediate Level Mistakes', displayOrder: 3 },
  { key: 'advancedMistakes', label: 'Advanced Level Mistakes', displayOrder: 4 },
  { key: 'proMistakes', label: 'Pro Level Mistakes', displayOrder: 5 },
  { key: 'commonMistakes', label: 'Common Mistakes', displayOrder: 6 },
] as const;

export const createEmptyStrokeVideoSections = (): StrokeVideoSection[] =>
  STROKE_VIDEO_SECTION_DEFINITIONS.map((section) => ({
    key: section.key,
    label: section.label,
    displayOrder: section.displayOrder,
    videoUrl: '',
    text: '',
    imageUrls: [],
    videos: [{ displayOrder: 1, sequence: 1, title: '', videoUrl: '', text: '', imageUrls: [], sponsorName: '', sponsorLogoUrl: '' }],
  }));

export const normalizeStrokeVideoSections = (
  sections: unknown,
  legacyReferenceVideos?: string
): StrokeVideoSection[] => {
  const input = Array.isArray(sections) ? sections : [];
  const legacyVideos = String(legacyReferenceVideos || '')
    .split(',')
    .map((url) => url.trim())
    .filter(Boolean);

  return STROKE_VIDEO_SECTION_DEFINITIONS.map((definition, index) => {
    const match = input.find((section: any) => section?.key === definition.key) as StrokeVideoSection | undefined;
    const rawVideos = Array.isArray(match?.videos) ? match.videos : [];
    const normalizedVideos = rawVideos
      .map((video, videoIndex) => ({
        displayOrder: Number(video?.displayOrder ?? video?.sequence ?? videoIndex + 1),
        sequence: Number(video?.displayOrder ?? video?.sequence ?? videoIndex + 1),
        title: video?.title || '',
        videoUrl: video?.videoUrl || '',
        text: video?.text || '',
        imageUrls: Array.isArray(video?.imageUrls) ? video.imageUrls.filter(Boolean) : [],
        sponsorName: video?.sponsorName || '',
        sponsorLogoUrl: video?.sponsorLogoUrl || '',
      }))
      .filter((video) => video.videoUrl || video.title || video.text || video.imageUrls.length > 0 || video.sponsorName || video.sponsorLogoUrl)
      .sort((a, b) => (a.displayOrder || 9999) - (b.displayOrder || 9999));
    const legacyVideoUrl = match?.videoUrl || legacyVideos[index] || '';
    const fallbackVideos = legacyVideoUrl
      ? [{
          displayOrder: 1,
          sequence: 1,
          title: match?.label || definition.label,
          videoUrl: legacyVideoUrl,
          text: match?.text || '',
          imageUrls: Array.isArray(match?.imageUrls) ? match.imageUrls.filter(Boolean) : [],
          sponsorName: '',
          sponsorLogoUrl: '',
        }]
      : [{ displayOrder: 1, sequence: 1, title: '', videoUrl: '', text: '', imageUrls: [], sponsorName: '', sponsorLogoUrl: '' }];
    const videos = normalizedVideos.length > 0 ? normalizedVideos : fallbackVideos;
    return {
      key: definition.key,
      label: definition.label,
      displayOrder: Number(match?.displayOrder || definition.displayOrder),
      videoUrl: videos.find((video) => video.videoUrl)?.videoUrl || legacyVideoUrl,
      text: match?.text || videos.find((video) => video.text)?.text || '',
      imageUrls: Array.isArray(match?.imageUrls) ? match.imageUrls.filter(Boolean) : [],
      videos,
    };
  });
};

export const getStrokeSectionVideoUrls = (sections: StrokeVideoSection[]): string[] =>
  Array.from(new Set(
    sections.flatMap((section) => {
      const videoUrls = Array.isArray(section.videos)
        ? section.videos.map((video) => String(video.videoUrl || '').trim()).filter(Boolean)
        : [];
      const legacyUrl = String(section.videoUrl || '').trim();
      return [...videoUrls, legacyUrl];
    })
  )).filter(Boolean);
